import React, { useState, useEffect, useRef, useMemo, useCallback, forwardRef, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useController } from 'react-hook-form';
import ApiService from '../../services/ApiService';
import { X, Check, AlertTriangle } from 'lucide-react';
import { FeedbackContext } from '../../contexts/FeedbackContext';
import {
  DropdownArrowContainer,
  InputContainer,
  SearchInput,
  OptionsList,
  OptionItem,
  OptionItemContent,
  OptionName,
  OptionDetails,
  PriceTags,
  PriceTag,
  NoOptionsMessage,
  LoadMoreButton,
  ClearButton,
  InputDiv,
  Label,
  Input,
  InputCell,
  SearchOptions,
  Option,
  SelectedItemsContainer,
  SelectedItem,
  SelectedItemText,
  RemoveIcon,
  ModalOverlay,
  ModalContent,
  ModalIcon,
  ModalTitle,
  ModalText,
  ErrorMessage,
  ModalButtons,
  CancelButton,
  DeleteButton,
  OptionContent,
  OptionLeft,
  CreateOptionStyle,
  PantoneSwatch,
} from './style';

interface Option {
  id: any;
  name: string;
  code?: string;
  disabled?: boolean;
  category?: string;
  type?: string;
  cnpj?: string;
  contato?: string;
  telefone?: string;
  contactName?: string;
  phone?: string;
  hex?: string;
  pantone?: { code: string; name?: string; hex?: string } | null;
  prices?: Array<{
    id?: number | string;
    price: string;
    unitOfMeasurement?: {
      id: number;
      name: string;
    };
  }>;
  stage?: {
    name: string;
    category: string;
    type: string;
  };
  [key: string]: any;
}

interface FieldConfig {
  key: string;
  label: string;
  getValue?: (option: Option) => any;
  render?: (value: any, option: Option) => React.ReactNode;
  condition?: (option: Option) => boolean;
  style?: React.CSSProperties;
}

interface EndpointConfig {
  baseUrl?: string;
  listPath: string;
  createPath?: string;
  deletePath?: string;
  searchParam?: string;
  limitParam?: string;
  offsetParam?: string;
  headers?: Record<string, string>;
  transformResponse?: (data: any) => Option[];
  transformCreatePayload?: (name: string, additionalData?: any) => any;
  transformCreateResponse?: (data: any) => Option;
  additionalParams?: Record<string, any>;
}

interface InfiniteSelectProps {
  name?: string;
  control?: any;

  value?: Option | null;
  onSelect?: (option: Option | null) => void;
  options?: Option[];

  placeholder?: string;
  searchPlaceholder?: string;
  emptyOptionsMessage?: string;
  errorOptionsMessage?: string;
  label?: string;

  fetchFunction?: (limit: number, offset: number, search?: string) => Promise<Option[]>;
  initialOptions?: Option[];

  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  searchValue?: string;
  onSearchChange?: (search: string) => void;

  disabled?: boolean;
  hasError?: boolean;
  onFocus?: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  getOptionLabel?: (option: Option) => string;

  validateSelection?: (selectedOption: Option | null) => boolean;

  fieldsConfig?: FieldConfig[];
  showDefaultFields?: boolean;

  mode?: 'readonly' | 'crud';
  onItemCreate?: (name: string) => Promise<Option>;
  onItemDelete?: (id: any) => Promise<void>;

  endpointConfig?: EndpointConfig;

  endpoint?: string;
  categoryId?: number;
  baseUrl?: string;

  allowCreate?: boolean;
  allowDelete?: boolean;
  minCreateLength?: number;
  showCreateOnlyIfNotExists?: boolean;
  showSelectedBelow?: boolean;
  dropdownZIndex?: number;
  dropdownForceFixed?: boolean;
  dropdownInline?: boolean;
  showPantoneSwatch?: boolean;
  inputStyle?: React.CSSProperties;

  /** Modo `crud`: trigger tipo célula (`InputCell`). Modo `readonly`: campo compacto para linhas de tabela. */
  triggerVariant?: 'default' | 'cell';
}

interface UseInfiniteDataOptions<T> {
  fetchFunction: (limit: number, offset: number, search?: string) => Promise<T[]>;
  pageSize: number;
  initialData?: T[];
}

interface UseInfiniteDataReturn<T> {
  data: T[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  resetAndSearch: (search: string) => void;
  searchValue: string;
  initialize: () => void;
}

function useInfiniteData<T extends { id: any }>({
  fetchFunction,
  pageSize,
  initialData = []
}: UseInfiniteDataOptions<T>): UseInfiniteDataReturn<T> {
  const [data, setData] = useState<T[]>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [searchValue, setSearchValue] = useState('');

  const isLoadingRef = useRef(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const fetchFunctionRef = useRef(fetchFunction);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    fetchFunctionRef.current = fetchFunction;
  }, [fetchFunction]);

  const performFetch = useCallback(async (search: string, currentOffset: number) => {
    if (isLoadingRef.current) return;

    console.log('[InfiniteSelect][debug] performFetch:start', {
      search,
      currentOffset,
      pageSize,
      hasInitialData: initialData.length > 0,
    })

    isLoadingRef.current = true;
    setIsLoading(true);

    try {
      const newData = await fetchFunctionRef.current(pageSize, currentOffset, search);
      const consumedCount = Number((newData as any)?._consumedCount);
      const requestedCount = Number((newData as any)?._requestedCount);
      const safeConsumedCount = Number.isFinite(consumedCount) ? consumedCount : newData.length;
      const safeRequestedCount = Number.isFinite(requestedCount) ? requestedCount : pageSize;

      console.log('[InfiniteSelect][debug] performFetch:result', {
        search,
        currentOffset,
        returnedLength: newData.length,
        consumedCount: safeConsumedCount,
        requestedCount: safeRequestedCount,
        firstItem: newData[0] ? { id: (newData[0] as any).id, name: (newData[0] as any).name } : null,
      })

      setData(prevData => {
        const combinedData = currentOffset === 0 ? newData : [...prevData, ...newData];
        const deduplicated = Array.from(new Map(combinedData.map(item => [item.id, item])).values());
        console.log('[InfiniteSelect][debug] performFetch:dedup', {
          prevLength: prevData.length,
          combinedLength: combinedData.length,
          deduplicatedLength: deduplicated.length,
        })
        return deduplicated;
      });

      setOffset(currentOffset + safeConsumedCount);
      setHasMore(safeConsumedCount === safeRequestedCount);

      console.log('[InfiniteSelect][debug] performFetch:state-update', {
        nextOffset: currentOffset + safeConsumedCount,
        hasMore: safeConsumedCount === safeRequestedCount,
      })

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [pageSize]);

  const resetAndSearch = useCallback((search: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchValue(search);

    console.log('[InfiniteSelect][debug] resetAndSearch', { search })

    searchTimeoutRef.current = setTimeout(() => {
      console.log('[InfiniteSelect][debug] resetAndSearch:debounced-fetch', { search })
      setData([]);
      setOffset(0);
      setHasMore(true);
      performFetch(search, 0);
    }, 300);
  }, [performFetch]);

  const loadMore = useCallback(() => {
    if (!hasMore || isLoading) return;
    console.log('[InfiniteSelect][debug] loadMore', {
      searchValue,
      offset,
      hasMore,
      isLoading,
    })
    performFetch(searchValue, offset);
  }, [hasMore, isLoading, performFetch, searchValue, offset]);

  const initialize = useCallback(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      performFetch('', 0);
    }
  }, [performFetch]);

  return {
    data,
    isLoading,
    hasMore,
    loadMore,
    resetAndSearch,
    searchValue,
    initialize
  };
}

const InfiniteSelect = forwardRef<HTMLInputElement, InfiniteSelectProps>(({
  name,
  control,
  value: controlledValue,
  onSelect: onSelectProp,
  options: controlledOptions,
  placeholder,
  searchPlaceholder = 'Buscar...',
  emptyOptionsMessage,
  errorOptionsMessage,
  label,
  fetchFunction,
  initialOptions = [],
  isLoading: controlledIsLoading,
  onLoadMore: controlledOnLoadMore,
  hasMore: controlledHasMore,
  searchValue: controlledSearchValue,
  onSearchChange: controlledOnSearchChange,
  disabled = false,
  hasError,
  onFocus: onFocusProp,
  onKeyDown,
  getOptionLabel,
  validateSelection,
  fieldsConfig = [],
  showDefaultFields = true,
  mode = 'readonly',
  onItemCreate,
  onItemDelete,
  endpointConfig,
  endpoint,
  categoryId,
  baseUrl,
  allowCreate = true,
  allowDelete = true,
  minCreateLength = 3,
  showCreateOnlyIfNotExists = false
  ,
  showSelectedBelow = true,
  dropdownZIndex = 9100,
  dropdownForceFixed = false,
  dropdownInline = false,
  showPantoneSwatch = true,
  triggerVariant = 'default',
  inputStyle,
}, ref) => {
  

  const [isOpen, setIsOpen] = useState(false);
  const [localSearchValue, setLocalSearchValue] = useState('');
  const [shouldOpenOnFocus, setShouldOpenOnFocus] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const dropdownOpenedAtRef = useRef<number>(0);
  const blurCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Option | null>(null);
  const [deleteError, setDeleteError] = useState<string>('');
  const [crudIsLoading, setCrudIsLoading] = useState(false);
  const { setFeedback } = useContext(FeedbackContext);
  const optionsListRef = useRef<HTMLDivElement>(null);
  const searchOptionsRef = useRef<HTMLUListElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const controllerProps = control && name ? useController({
    name,
    control,
  }) : null;

  const fieldValue = controllerProps?.field.value || controlledValue;
  const fieldOnChange = controllerProps?.field.onChange || onSelectProp;
  const fieldRef = controllerProps?.field.ref || ref;

  const setRefs = useCallback((element: HTMLInputElement | null) => {
    if (fieldRef && typeof fieldRef === 'function') {
      fieldRef(element);
    } else if (fieldRef && 'current' in fieldRef) {
      (fieldRef as any).current = element;
    }
    localInputRef.current = element;
  }, [fieldRef]);

  const [coords, setCoords] = useState<{ left: number; top?: number; bottom?: number; width: number; maxHeight: number } | null>(null);
  const debugInstanceId = useMemo(() => `${name || 'no-name'}|${label || placeholder || 'no-label'}|${mode}`, [name, label, placeholder, mode]);

  const clearBlurCloseTimeout = useCallback(() => {
    if (blurCloseTimeoutRef.current !== null) {
      clearTimeout(blurCloseTimeoutRef.current);
      blurCloseTimeoutRef.current = null;
    }
  }, []);

  const getEndpointConfig = useCallback((): EndpointConfig | null => {
    if (endpointConfig) {
      return {
        searchParam: 'query',
        limitParam: 'limit',
        offsetParam: 'offset',
        ...endpointConfig
      };
    }

    if (endpoint) {
      let apiBaseUrl = '';

      if (baseUrl) {
        if (endpoint.startsWith('util/')) {
          apiBaseUrl = `${baseUrl}/resource`;
        } else {
          apiBaseUrl = baseUrl;
        }
      } else {
        apiBaseUrl = `${process.env.REACT_APP_GATEWAY_BASE_URL}/services/service-stage`;
      }

      return {
        baseUrl: apiBaseUrl,
        listPath: `/${endpoint}`,
        createPath: `/${endpoint}`,
        deletePath: `/${endpoint}`,
        searchParam: 'query',
        limitParam: 'limit',
        offsetParam: 'offset'
      };
    }

    return null;
  }, [endpointConfig, endpoint, baseUrl]);

  const buildUrl = useCallback((path: string, config?: EndpointConfig) => {
    const activeConfig = config || getEndpointConfig();
    if (!activeConfig) return '';

    const baseUrlToUse = activeConfig.baseUrl || '';
    return `${baseUrlToUse}${path}`;
  }, [getEndpointConfig]);

  const endpointFetchFunction = useCallback(async (limit: number, offset: number, search?: string) => {
    const config = getEndpointConfig();
    if (!config || !config.listPath) return [];

    try {




      if (config.listPath === '/colors' && (!config.additionalParams || Object.keys(config.additionalParams).length === 0)) {
        return [];
      }

      const params: any = {};

      if (search) {
        params[config.searchParam || 'query'] = search;
      }

      params[config.limitParam || 'limit'] = limit;
      params[config.offsetParam || 'offset'] = offset;

      if (config.additionalParams) {
        Object.assign(params, config.additionalParams);
      }

      const url = buildUrl(config.listPath, config);

      const response = await ApiService.infiniteSelect.get(url, { params });

      let data = response.data;

      if (config.transformResponse) {
        data = config.transformResponse(data);
      }

      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      return [];
    }
  }, [getEndpointConfig, buildUrl]);

  const activeFetchFunction = useMemo(() => {
    if (fetchFunction) return fetchFunction;
    if (mode === 'crud' && getEndpointConfig()) return endpointFetchFunction;
    return () => Promise.resolve([]);
  }, [fetchFunction, mode, getEndpointConfig, endpointFetchFunction]);

  const {
    data: infiniteData,
    isLoading: infiniteIsLoading,
    hasMore: infiniteHasMore,
    loadMore: infiniteLoadMore,
    resetAndSearch: infiniteResetAndSearch,
    searchValue: infiniteSearchValue,
    initialize: infiniteInitialize
  } = useInfiniteData({
    fetchFunction: activeFetchFunction,
    pageSize: 10,
    initialData: initialOptions,
  });

  const activeOptions = controlledOptions || infiniteData;
  const isCurrentlyLoading = controlledIsLoading !== undefined ? controlledIsLoading : infiniteIsLoading;
  const currentlyHasMore = controlledHasMore !== undefined ? controlledHasMore : infiniteHasMore;
  const handleLoadMore = controlledOnLoadMore || infiniteLoadMore;
  const currentSearchTerm = controlledSearchValue !== undefined ? controlledSearchValue : infiniteSearchValue;

  const searchChangeHandler = controlledOnSearchChange || infiniteResetAndSearch;

  useEffect(() => {
    const root = optionsListRef.current || searchOptionsRef.current;
    if (!root || !loadMoreRef.current) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && currentlyHasMore && !isCurrentlyLoading) {
        handleLoadMore();
      }
    }, {
      root,
      threshold: 0.1
    });

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [currentlyHasMore, isCurrentlyLoading, handleLoadMore, isOpen]);

  const getDisplayValue = useCallback((val: Option | null) => {
    if (!val) return '';
    if (typeof getOptionLabel === 'function') return getOptionLabel(val);

    if (typeof val !== 'object' || !val.name) return '';

    return val.code ? `${val.code} - ${val.name}` : val.name;
  }, [getOptionLabel]);

  useEffect(() => {
    if (!isOpen) {
      setLocalSearchValue(getDisplayValue(fieldValue));
    }
  }, [fieldValue, getDisplayValue, isOpen]);

  useEffect(() => {
    return () => {
      clearBlurCloseTimeout();
    };
  }, [clearBlurCloseTimeout]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (!isOpen) return;
    const elapsedSinceOpen = Date.now() - dropdownOpenedAtRef.current;
    const recentlyOpened = elapsedSinceOpen < 180;
    if (recentlyOpened) {
      return;
    }
    const target = event.target as Node;
    const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const isTargetInPath = (element: Node | null | undefined) => {
      if (!element) return false;
      return eventPath.includes(element);
    };
    const isInsideContainer =
      (containerRef.current && containerRef.current.contains(target)) ||
      (localInputRef.current && localInputRef.current.contains(target)) ||
      isTargetInPath(containerRef.current) ||
      isTargetInPath(localInputRef.current);
    const isInsidePortal =
      (optionsListRef.current && optionsListRef.current.contains(target)) ||
      isTargetInPath(optionsListRef.current);
    const isInsideSearchPortal =
      (searchOptionsRef.current && searchOptionsRef.current.contains(target)) ||
      isTargetInPath(searchOptionsRef.current);
    if (!isInsideContainer && !isInsidePortal && !isInsideSearchPortal) {
      console.log('[InfiniteSelect][debug] outside-close', {
        debugInstanceId,
        isInsideContainer,
        isInsidePortal,
        isInsideSearchPortal,
        elapsedSinceOpen,
        targetNodeName: (target as any)?.nodeName,
      });
      setIsOpen(false);
    }
  }, [isOpen, debugInstanceId]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    dropdownOpenedAtRef.current = Date.now();

    const updateCoords = () => {
      const el = localInputRef.current || containerRef.current?.querySelector('input');
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const MARGIN = 8;
      const MAX_HEIGHT = 300;
      const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
      const spaceAbove = rect.top - MARGIN;
      if (spaceBelow >= 200 || spaceBelow >= spaceAbove) {
        const maxHeight = Math.min(MAX_HEIGHT, spaceBelow);
        setCoords({ left: rect.left, top: rect.bottom + MARGIN, width: rect.width, maxHeight });
      } else {
        const maxHeight = Math.min(MAX_HEIGHT, spaceAbove);
        setCoords({ left: rect.left, bottom: window.innerHeight - rect.top + MARGIN, width: rect.width, maxHeight });
      }
    };

    updateCoords();
    window.addEventListener('resize', updateCoords);
    window.addEventListener('scroll', updateCoords, true);
    return () => {
      window.removeEventListener('resize', updateCoords);
      window.removeEventListener('scroll', updateCoords, true);
    };
  }, [isOpen]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (disabled) return;
    clearBlurCloseTimeout();

    console.log('[InfiniteSelect][debug] focus', {
      debugInstanceId,
      shouldOpenOnFocus,
      hasFieldValue: !!fieldValue,
      currentSearchValue,
    });

    if (!shouldOpenOnFocus) {
      setShouldOpenOnFocus(true);
    }

    setIsOpen(true);

    if (mode === 'crud') {
      setIsEditing(true);
    }

    if (onFocusProp) {
      onFocusProp();
    }

    if (infiniteInitialize) {
      infiniteInitialize();
    }

    if (fieldValue && shouldOpenOnFocus) {
      if (mode === 'crud') {
        setLocalSearchValue(fieldValue.name);
      } else {
        setLocalSearchValue(fieldValue.name);
      }
      setTimeout(() => {
        const length = e.target.value.length;
        e.target.setSelectionRange(length, length);
      }, 50);
    } else if (!fieldValue) {
      setLocalSearchValue('');
    }
  };

  const handleMouseDownInput = useCallback(() => {
    if (disabled) return;
    clearBlurCloseTimeout();
    if (!isOpen) {
      setIsOpen(true);
      if (infiniteInitialize) {
        infiniteInitialize();
      }
    }
  }, [clearBlurCloseTimeout, disabled, infiniteInitialize, isOpen]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    console.log('[InfiniteSelect][debug] blur-schedule-close', {
      debugInstanceId,
      relatedTargetNodeName: (e.relatedTarget as any)?.nodeName,
    });
    clearBlurCloseTimeout();
    blurCloseTimeoutRef.current = setTimeout(() => {
      console.log('[InfiniteSelect][debug] blur-timeout-close', {
        debugInstanceId,
      });
      setIsOpen(false);
      if (mode === 'crud') {
        setIsEditing(false);
      }
      blurCloseTimeoutRef.current = null;
    }, 300);
  }, [clearBlurCloseTimeout, debugInstanceId, mode]);

  const handleSelect = useCallback((option: Option) => {
    console.log('[InfiniteSelect] handleSelect chamado → option:', option);
    if (option.disabled) {
      console.log('[InfiniteSelect] handleSelect → opção desabilitada, ignorando');
      return;
    }

    if (validateSelection) {
      const valid = validateSelection(option);
      console.log('[InfiniteSelect] handleSelect → validateSelection:', valid);
      if (valid) {
        if (fieldOnChange) {
          console.log('[InfiniteSelect] handleSelect → chamando fieldOnChange (com validateSelection)');
          fieldOnChange(option);
        }
      }
    } else {
      if (fieldOnChange) {
        console.log('[InfiniteSelect] handleSelect → chamando fieldOnChange sem validação');
        fieldOnChange(option);
      } else {
        console.warn('[InfiniteSelect] handleSelect → fieldOnChange é nulo/undefined!');
      }
    }

    clearBlurCloseTimeout();
    setLocalSearchValue(getDisplayValue(option));
    setIsOpen(false);
    setShouldOpenOnFocus(true);
  }, [clearBlurCloseTimeout, fieldOnChange, validateSelection, getDisplayValue, fieldRef]);

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (fieldOnChange) {
      fieldOnChange(null);
    }
    setLocalSearchValue('');
    infiniteResetAndSearch('');
  };

  const handleSearchChange = (val: string) => {
    if (!isOpen) {
      setIsOpen(true);
    }
    setLocalSearchValue(val);

    console.log('[InfiniteSelect][debug] handleSearchChange', {
      value: val,
      mode,
      isOpen,
      hasControlledSearchHandler: !!controlledOnSearchChange,
    })

    if (mode === 'crud') {
      setIsEditing(true);
      setSelectedIndex(0);
    }

    if (searchChangeHandler) {
      searchChangeHandler(val);
    }
  };

  const currentSearchValue = localSearchValue;

  const handleCreateItem = useCallback(async () => {
    if (!localSearchValue.trim() || crudIsLoading || localSearchValue.length < minCreateLength) {
      return;
    }

    setCrudIsLoading(true);
    try {
      let newItem: Option;

      if (onItemCreate) {
        newItem = await onItemCreate(localSearchValue);
      } else {
        const config = getEndpointConfig();
        if (!config) {
          throw new Error('Nenhuma função de criação ou configuração de endpoint fornecida');
        }

        let payload: any;
        if (config.transformCreatePayload) {
          const additionalData = categoryId ? { categoryId } : {};
          payload = config.transformCreatePayload(localSearchValue, additionalData);
        } else {
          payload = { name: localSearchValue };
          if (categoryId) payload.categoryId = categoryId;
          if (config.additionalParams) {
            payload = { ...payload, ...config.additionalParams };
          }
        }

        const createPath = config.createPath || config.listPath;
        const url = buildUrl(createPath, config);
        const requestConfig: any = {};

        if (config.headers) {
          requestConfig.headers = config.headers;
        }

        const response = await ApiService.infiniteSelect.post(url, payload, requestConfig);

        if (config.transformCreateResponse) {
          newItem = config.transformCreateResponse(response.data);
        } else {
          newItem = response.data;
        }
      }

      setLocalSearchValue(getDisplayValue(newItem));
      setIsOpen(false);
      setIsEditing(false);

      if (fieldOnChange) {
        fieldOnChange(newItem);
      }

      if (!controlledOptions) {
        infiniteResetAndSearch('');
      }
    } catch (error) {
      console.error('Erro ao criar item:', error);
      // Keep dropdown open and text intact so user can fix the issue and retry
      setIsOpen(true);
      setIsEditing(true);
    } finally {
      setCrudIsLoading(false);
    }
  }, [localSearchValue, crudIsLoading, minCreateLength, onItemCreate, getEndpointConfig, categoryId, buildUrl, getDisplayValue, fieldOnChange, controlledOptions, infiniteResetAndSearch]);

  const handleRemoveItem = useCallback(async (id: any) => {
    try {
      if (onItemDelete) {
        await onItemDelete(id);
      } else {
        const config = getEndpointConfig();
        if (!config) {
          throw new Error('Nenhuma função de exclusão ou configuração de endpoint fornecida');
        }

        const deletePath = config.deletePath || config.listPath;
        const url = buildUrl(`${deletePath}/${id}`, config);
        const requestConfig: any = {};

        if (config.headers) {
          requestConfig.headers = config.headers;
        }

        await ApiService.infiniteSelect.delete(url, requestConfig);
      }

      if (fieldValue && fieldValue.id === id) {
        if (fieldOnChange) {
          fieldOnChange(null);
        }
        setLocalSearchValue('');
      }

      if (!controlledOptions) {
        infiniteResetAndSearch('');
      }
    } catch (error: any) {

      let errorMessage = 'Erro ao excluir item';

      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.status === 400) {
        errorMessage = 'Não é possível excluir este item pois está sendo usado.';
      } else if (error?.response?.status === 404) {
        errorMessage = 'Item não encontrado.';
      } else if (error?.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente.';
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (typeof error?.response?.data === 'string') {
        errorMessage = error.response.data;
      } else if (Array.isArray(error?.response?.data?.message)) {
        errorMessage = error.response.data.message[0];
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setDeleteError(errorMessage);
      throw error;
    }
  }, [onItemDelete, getEndpointConfig, buildUrl, fieldValue, fieldOnChange, controlledOptions, infiniteResetAndSearch]);

  const handleRemoveFromDropdown = useCallback(async (e: React.MouseEvent, item: Option) => {
    e.stopPropagation();
    setItemToDelete(item);
    setShowConfirmModal(true);
    setDeleteError('');
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;

    try {
      await handleRemoveItem(itemToDelete.id);
      setShowConfirmModal(false);
      setItemToDelete(null);
      setDeleteError('');
      setFeedback({
        isOpen: true,
        message: `O campo "${itemToDelete.name}" foi excluído com sucesso.`,
        statusOk: true,
      });
    } catch (error) {
      console.error('Erro ao confirmar exclusão:', error);
    }
  }, [itemToDelete, handleRemoveItem, setFeedback]);

  const cancelDelete = useCallback(() => {
    setShowConfirmModal(false);
    setItemToDelete(null);
    setDeleteError('');
  }, []);

  const isItemSelected = useCallback((item: Option) => {
    return fieldValue && fieldValue.id === item.id;
  }, [fieldValue]);

  const handleSetOptionByKey = useCallback((e: React.KeyboardEvent) => {
    if (mode !== 'crud') {
      if (onKeyDown) {
        onKeyDown(e as React.KeyboardEvent<HTMLInputElement>);
      }
      return;
    }

    if (e.key === 'ArrowDown' && selectedIndex < activeOptions.length) {
      e.preventDefault();
      setSelectedIndex(prev => prev + 1);
    }
    if (e.key === 'ArrowUp' && selectedIndex > 0) {
      e.preventDefault();
      setSelectedIndex(prev => prev - 1);
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex < activeOptions.length) {
        handleSelect(activeOptions[selectedIndex]);
      } else if (localSearchValue.trim() && isEditing && isOpen && localSearchValue.length >= minCreateLength) {
        handleCreateItem();
      }
    }
    if (e.key === 'Tab') {
      setIsOpen(false);
      setIsEditing(false);
    }
  }, [mode, selectedIndex, activeOptions.length, localSearchValue, isEditing, isOpen, minCreateLength, handleSelect, handleCreateItem, onKeyDown]);

  const getDisplayLabel = (option: Option) => {
    if (typeof getOptionLabel === 'function') {
      return getOptionLabel(option);
    }
    return option.name;
  };

  const getOptionSwatchHex = useCallback((option: Option): string | undefined => {
    const normalizeHexCandidate = (candidate: any): string | undefined => {
      if (typeof candidate !== 'string') return undefined
      const trimmed = candidate.trim()
      if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) return trimmed
      if (/^([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) return `#${trimmed}`
      return undefined
    }

    const visited = new Set<any>()

    const findHexRecursively = (input: any, depth: number): string | undefined => {
      if (depth > 6) return undefined

      const directHex = normalizeHexCandidate(input)
      if (directHex) return directHex

      if (!input || typeof input !== 'object') return undefined
      if (visited.has(input)) return undefined
      visited.add(input)

      if (Array.isArray(input)) {
        for (const item of input) {
          const nestedHex = findHexRecursively(item, depth + 1)
          if (nestedHex) return nestedHex
        }
        return undefined
      }

      const prioritizedKeys = [
        '_swatchHex',
        'hex',
        'Hex',
        'pantone',
        'Pantone',
        'color',
        'Color',
        'itemColor',
        'ItemColor',
        'collectionColor',
        'CollectionColor',
      ]

      for (const key of prioritizedKeys) {
        if (!(key in input)) continue
        const nestedHex = findHexRecursively((input as any)[key], depth + 1)
        if (nestedHex) return nestedHex
      }

      for (const nestedValue of Object.values(input)) {
        const nestedHex = findHexRecursively(nestedValue, depth + 1)
        if (nestedHex) return nestedHex
      }

      return undefined
    }

    return findHexRecursively(option, 0)
  }, [])

  const isPantoneSwatchAllowedForOption = useCallback((option: Option): boolean => {
    if (!showPantoneSwatch) return false

    const integrationModuleId = String((option as any)?._integrationModuleId || '').trim().toLowerCase()
    const integrationFieldKey = String((option as any)?._integrationFieldKey || '').trim().toLowerCase()

    if (!integrationModuleId && !integrationFieldKey) {
      return true
    }

    const isColorField = integrationFieldKey === 'color' || integrationFieldKey === 'colors'
    if (!isColorField) return false

    return integrationModuleId === 'resources' || integrationModuleId === 'fashion-stock'
  }, [showPantoneSwatch])

  const handleOptionsScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
    if (!currentlyHasMore || isCurrentlyLoading) return
    const target = event.currentTarget
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 24
    if (nearBottom) {
      handleLoadMore()
    }
  }, [currentlyHasMore, isCurrentlyLoading, handleLoadMore])

  const getDefaultFields = (): FieldConfig[] => [
    {
      key: 'id',
      label: 'ID',
      getValue: (option) => option.id,
      render: (value) => typeof value === 'object' ? value?.id || value?.name || 'N/A' : value,
      condition: (option) => !!option.id
    }
  ];

  const renderField = (fieldConfig: FieldConfig, option: Option) => {
    if (fieldConfig.condition && !fieldConfig.condition(option)) {
      return null;
    }

    const value = fieldConfig.getValue ? fieldConfig.getValue(option) : option[fieldConfig.key];

    if (value === undefined || value === null || value === '') {
      return null;
    }

    const displayValue = fieldConfig.render ? fieldConfig.render(value, option) : value;

    return (
      <span key={fieldConfig.key} style={fieldConfig.style}>
        <strong>{fieldConfig.label}:</strong> {displayValue}
      </span>
    );
  };

  const shouldShowDetails = (option: Option) => {
    const allFields = showDefaultFields ? [...getDefaultFields(), ...fieldsConfig] : fieldsConfig;
    return allFields.some(field => {
      if (field.condition && !field.condition(option)) {
        return false;
      }
      const value = field.getValue ? field.getValue(option) : option[field.key];
      return value !== undefined && value !== null && value !== '';
    });
  };


  const searchOptionsStyle = (() => {
    if (dropdownInline) {
      return { position: 'absolute', left: 0, top: 'calc(100% + 8px)', width: '100%', zIndex: dropdownZIndex };
    }
    if (dropdownForceFixed) {
      return { position: 'fixed', left: coords?.left || 0, ...(coords?.bottom !== undefined ? { bottom: coords.bottom, top: 'unset' } : { top: coords?.top || 0 }), width: coords?.width || undefined, maxHeight: coords?.maxHeight, overflowY: 'auto', zIndex: dropdownZIndex };
    }
    if (coords) {
      return { position: 'fixed', left: coords.left, ...(coords.bottom !== undefined ? { bottom: coords.bottom, top: 'unset' } : { top: coords.top }), width: coords.width, maxHeight: coords.maxHeight, overflowY: 'auto', zIndex: dropdownZIndex };
    }
    return {};
  })();

  const renderedOptionItems = useMemo(() => activeOptions.map((option) => {
    const allFields = showDefaultFields ? [...getDefaultFields(), ...fieldsConfig] : fieldsConfig;
    const optSwatchHex = isPantoneSwatchAllowedForOption(option) ? getOptionSwatchHex(option) : undefined;

    return (
      <OptionItem
        key={option.id}
        onClick={() => handleSelect(option)}
        disabled={option.disabled}
        isHighlighted={fieldValue && fieldValue.id === option.id}
      >
        <OptionItemContent>
          <OptionName style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {optSwatchHex !== undefined && (
              <PantoneSwatch hex={optSwatchHex} title={optSwatchHex} />
            )}
            {getDisplayLabel(option)}
          </OptionName>
          {shouldShowDetails(option) && (
            <OptionDetails>
              {allFields.map(field => renderField(field, option))}
            </OptionDetails>
          )}
          {option.prices && option.prices.length > 0 && (
            <PriceTags>
              {option.prices.map((price: any, index: number) => (
                <PriceTag key={index}>
                  R$ {parseFloat(price.price).toFixed(2)}/{price.unitOfMeasurement?.name || 'unidade'}
                </PriceTag>
              ))}
            </PriceTags>
          )}
        </OptionItemContent>
      </OptionItem>
    );
  }), [activeOptions, fieldValue, handleSelect, fieldsConfig, showDefaultFields, getOptionSwatchHex, isPantoneSwatchAllowedForOption]);

  if (mode === 'crud') {
    const TriggerInput = triggerVariant === 'cell' ? InputCell : Input;
    return (
      <>
      <InputDiv ref={containerRef}>
        {label && <Label>{label}</Label>}
        <TriggerInput
          ref={setRefs}
          type="text"
          placeholder={placeholder}
          value={currentSearchValue}
          title={disabled ? "Campo desabilitado" : ""}
          onMouseDown={handleMouseDownInput}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={(e) => handleSearchChange(e.target.value)}
          onKeyDown={(e) => handleSetOptionByKey(e)}
          disabled={crudIsLoading || disabled}
          name={name}
          style={inputStyle}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          data-lpignore="true"
          data-form-type="other"
        />
        {isOpen && coords && !disabled &&
          ReactDOM.createPortal(
          <SearchOptions ref={searchOptionsRef} style={searchOptionsStyle as React.CSSProperties} onScroll={handleOptionsScroll} onMouseDown={(e) => e.preventDefault()}>
            {isCurrentlyLoading && <Option style={{ cursor: 'default', backgroundColor: '#f8f9fa', color: '#6c757d' }}>Carregando...</Option>}
            {!isCurrentlyLoading && activeOptions.length === 0 && <Option style={{ cursor: 'default', backgroundColor: '#f8f9fa', color: '#6c757d' }}>{currentSearchValue ? 'Nenhum resultado encontrado' : 'Nenhum item disponível'}</Option>}
            {!isCurrentlyLoading && activeOptions.map((item, index) => {
              const swatchHex = isPantoneSwatchAllowedForOption(item) ? getOptionSwatchHex(item) : undefined;
              return (
                <Option
                  key={index}
                  onClick={() => handleSelect(item)}
                  isSelected={index === selectedIndex}
                >
                  <OptionContent>
                    <OptionLeft>
                      {isItemSelected(item) && <Check size={14} color="#28a745" />}
                      {swatchHex !== undefined && (
                        <PantoneSwatch hex={swatchHex} title={swatchHex} />
                      )}
                      <span>{getDisplayLabel(item)}</span>
                    </OptionLeft>
                    {allowDelete && (
                      <X
                        size={14}
                        style={{ cursor: 'pointer', color: '#dc3545' }}
                        onClick={(e) => handleRemoveFromDropdown(e, item)}
                      />
                    )}
                  </OptionContent>
                </Option>
              );
            })}
            {allowCreate && currentSearchValue.trim() && currentSearchValue.length >= minCreateLength && (() => {
              const exactMatch = activeOptions.find(item =>
                item.name && item.name.toLowerCase() === currentSearchValue.toLowerCase()
              );

              if (showCreateOnlyIfNotExists && exactMatch) {
                return null;
              }

              if (!exactMatch) {
                return (
                  <Option
                    onClick={handleCreateItem}
                    isSelected={selectedIndex === activeOptions.length}
                  >
                    <CreateOptionStyle>
                      + Criar &ldquo;{currentSearchValue}&rdquo;
                    </CreateOptionStyle>
                  </Option>
                );
              }

              return null;
            })()}
            {currentlyHasMore && (
              <div ref={loadMoreRef} style={{ height: '1px', visibility: 'hidden' }} />
            )}
            {isCurrentlyLoading && <Option style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Carregando mais...</Option>}
          </SearchOptions>,
          document.body
          )
        }

        {fieldValue && showSelectedBelow && (
          <SelectedItemsContainer>
            <SelectedItem>
              <SelectedItemText>{getDisplayValue(fieldValue)}</SelectedItemText>
              <RemoveIcon disabled={disabled}>
                <X
                  size={16}
                  onClick={disabled ? undefined : handleClear}
                />
              </RemoveIcon>
            </SelectedItem>
          </SelectedItemsContainer>
        )}

        {showConfirmModal &&
          ReactDOM.createPortal(
            <ModalOverlay>
              <ModalContent>
                <ModalIcon>
                  <AlertTriangle size={48} />
                </ModalIcon>
                <ModalTitle>
                  Confirmar Exclusão
                </ModalTitle>
                <ModalText>
                  Tem certeza que deseja excluir &ldquo;{itemToDelete?.name}&rdquo;?
                </ModalText>

                {deleteError && (
                  <ErrorMessage>
                    {deleteError}
                  </ErrorMessage>
                )}

                <ModalButtons>
                  <CancelButton onClick={cancelDelete}>
                    Cancelar
                  </CancelButton>
                  <DeleteButton onClick={confirmDelete}>
                    Excluir
                  </DeleteButton>
                </ModalButtons>
              </ModalContent>
            </ModalOverlay>,
            document.body
          )
        }
      </InputDiv>
      </>
    );
  }

  const readonlyCellTrigger = triggerVariant === 'cell';

  return (
    <InputContainer ref={containerRef}>
      {label && <Label style={{ marginBottom: '0.5rem' }}>{label}</Label>}
      <SearchInput
        ref={setRefs}
        type="text"
        value={currentSearchValue}
        onChange={(e) => handleSearchChange(e.target.value)}
        onMouseDown={handleMouseDownInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        hasError={!!hasError}
        isOpen={isOpen}
        name={name}
        style={inputStyle}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        data-lpignore="true"
        data-form-type="other"
        $cell={readonlyCellTrigger}
        $cellWithClear={readonlyCellTrigger && !!fieldValue}
      />
      {fieldValue && (
        <ClearButton onClick={handleClear} title="Limpar" type="button" $cell={readonlyCellTrigger}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M1 1L11 11M1 11L11 1" stroke="#666" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </ClearButton>
      )}
      <DropdownArrow isOpen={isOpen} $cell={readonlyCellTrigger} />

      {isOpen && coords && ReactDOM.createPortal(
        <OptionsList isOpen={isOpen} ref={optionsListRef} style={searchOptionsStyle as React.CSSProperties} onScroll={handleOptionsScroll} onMouseDown={(e) => e.preventDefault()}>
        {isCurrentlyLoading && <OptionItem>Carregando...</OptionItem>}
        {!isCurrentlyLoading && activeOptions.length === 0 && <NoOptionsMessage>{errorOptionsMessage || (currentSearchValue ? 'Nenhum resultado encontrado' : (emptyOptionsMessage || searchPlaceholder || 'Nenhum item'))}</NoOptionsMessage>}
        {renderedOptionItems}
        {currentlyHasMore && (
          <div ref={loadMoreRef} style={{ height: '1px', visibility: 'hidden' }} />
        )}
        {isCurrentlyLoading && <OptionItem style={{ textAlign: 'center', color: '#666', fontStyle: 'italic' }}>Carregando mais...</OptionItem>}
        </OptionsList>,
        document.body
      )}
    </InputContainer>
  );
});

const DropdownArrow: React.FC<{ isOpen: boolean; $cell?: boolean }> = ({ isOpen, $cell }) => {
  return (
    <DropdownArrowContainer isOpen={isOpen} $cell={$cell}>
      <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1 1.5L6 6.5L11 1.5" stroke="#696969" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </DropdownArrowContainer>
  );
};

InfiniteSelect.displayName = 'InfiniteSelect';

export type { Option, FieldConfig, EndpointConfig, InfiniteSelectProps };
export default InfiniteSelect;
